/**
 * Sortable, filterable list of pilot visitors.
 */

import React, {useMemo, useState} from 'react';
import {
  Alert,
  Box,
  Chip,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import {useQuery} from '@apollo/client';
import {PILOT_VISITORS} from '../../graphql/pilotDashboardQueries';

const STAGE_LABEL: Record<string, string> = {
  VISITED: 'Visited',
  ENGAGED_SIMULATOR: 'Engaged · Sim',
  ENGAGED_CALCULATOR: 'Engaged · ROI',
  OPENED_DIALOG: 'Opened dialog',
  SUBMITTED_LEAD: 'Submitted lead',
};

const STAGE_COLOR: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'info'> = {
  VISITED: 'default',
  ENGAGED_SIMULATOR: 'info',
  ENGAGED_CALCULATOR: 'info',
  OPENED_DIALOG: 'warning',
  SUBMITTED_LEAD: 'success',
};

interface VisitorRow {
  id: string;
  identity: {
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    company?: string | null;
    title?: string | null;
  };
  attribution: {
    source: string;
    campaign?: string | null;
    medium?: string | null;
  };
  firstSeenAt: string;
  lastSeenAt: string;
  identifiedAt?: string | null;
  sessionCount: number;
  eventCount: number;
  leadCount: number;
  stage: string;
}

interface Props {
  onSelect: (visitorId: string) => void;
}

function formatRelative(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const diffMs = Date.now() - t;
  if (diffMs < 60_000) return 'just now';
  if (diffMs < 60 * 60_000) return `${Math.round(diffMs / 60_000)}m ago`;
  if (diffMs < 24 * 60 * 60_000)
    return `${Math.round(diffMs / (60 * 60_000))}h ago`;
  return new Date(t).toLocaleDateString();
}

function fullName(identity: VisitorRow['identity']): string {
  return [identity.firstName, identity.lastName]
    .filter(Boolean)
    .join(' ');
}

export default function PilotVisitorTable({onSelect}: Props) {
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState<string>('');
  const [source, setSource] = useState<string>('');
  const [campaign, setCampaign] = useState<string>('');
  const [sortField, setSortField] = useState<
    'LAST_SEEN' | 'FIRST_SEEN' | 'LEAD_COUNT' | 'EVENT_COUNT'
  >('LAST_SEEN');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const filter = useMemo(
    () => ({
      search: search.trim() || undefined,
      stage: stage || undefined,
      source: source.trim() || undefined,
      campaign: campaign.trim() || undefined,
    }),
    [search, stage, source, campaign],
  );

  const {data, loading, error} = useQuery(PILOT_VISITORS, {
    variables: {
      filter,
      sort: {field: sortField, direction: sortDirection},
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    },
    fetchPolicy: 'cache-and-network',
  });

  const result = data?.pilotVisitors;
  const rows: VisitorRow[] = result?.visitors || [];
  const total: number = result?.total || 0;

  const toggleSort = (
    field: 'LAST_SEEN' | 'FIRST_SEEN' | 'LEAD_COUNT' | 'EVENT_COUNT',
  ) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(field);
      setSortDirection('DESC');
    }
  };

  return (
    <Box>
      <Stack
        direction={{xs: 'column', md: 'row'}}
        spacing={1.5}
        sx={{mb: 2}}
      >
        <TextField
          size="small"
          label="Search (email, name, company)"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          fullWidth
        />
        <TextField
          size="small"
          label="Source"
          value={source}
          onChange={(e) => {
            setSource(e.target.value);
            setPage(0);
          }}
          sx={{minWidth: 160}}
        />
        <TextField
          size="small"
          label="Campaign"
          value={campaign}
          onChange={(e) => {
            setCampaign(e.target.value);
            setPage(0);
          }}
          sx={{minWidth: 160}}
        />
        <TextField
          select
          size="small"
          label="Stage"
          value={stage}
          onChange={(e) => {
            setStage(e.target.value);
            setPage(0);
          }}
          sx={{minWidth: 200}}
        >
          <MenuItem value="">All</MenuItem>
          {Object.entries(STAGE_LABEL).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {error && (
        <Alert severity="error" sx={{mb: 2}}>
          {error.message}
        </Alert>
      )}

      <Paper variant="outlined" sx={{overflow: 'hidden'}}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Identity</TableCell>
                <TableCell>Source / campaign</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell
                  align="right"
                  onClick={() => toggleSort('LEAD_COUNT')}
                  sx={{cursor: 'pointer', userSelect: 'none'}}
                >
                  Leads {sortField === 'LEAD_COUNT' && (sortDirection === 'DESC' ? '↓' : '↑')}
                </TableCell>
                <TableCell
                  align="right"
                  onClick={() => toggleSort('EVENT_COUNT')}
                  sx={{cursor: 'pointer', userSelect: 'none'}}
                >
                  Events {sortField === 'EVENT_COUNT' && (sortDirection === 'DESC' ? '↓' : '↑')}
                </TableCell>
                <TableCell
                  onClick={() => toggleSort('LAST_SEEN')}
                  sx={{cursor: 'pointer', userSelect: 'none'}}
                >
                  Last seen {sortField === 'LAST_SEEN' && (sortDirection === 'DESC' ? '↓' : '↑')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{py: 4}}>
                    <Typography variant="body2" color="text.secondary">
                      No visitors match these filters yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row) => {
                const name = fullName(row.identity);
                return (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => onSelect(row.id)}
                    sx={{cursor: 'pointer'}}
                  >
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography
                          variant="body2"
                          sx={{fontWeight: 500}}
                        >
                          {row.identity.email || '— anonymous —'}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {[name, row.identity.title, row.identity.company]
                            .filter(Boolean)
                            .join(' · ') || row.id.slice(0, 12)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography variant="body2">
                          {row.attribution.source}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {row.attribution.campaign || '—'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={STAGE_LABEL[row.stage] || row.stage}
                        color={STAGE_COLOR[row.stage] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{row.leadCount}</TableCell>
                    <TableCell align="right">{row.eventCount}</TableCell>
                    <TableCell>{formatRelative(row.lastSeenAt)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
}
